import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message, Modal, Space, Tabs, Table, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { PlusOutlined, UploadOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { CREATE_ADJUSTMENT_TASK, GET_ADJUSTMENT_TASKS } from '../../graphql/queries/settlement/adjustmentQueries';
import { GET_BILL_DETAIL } from '../../graphql/queries/settlement/getSettlementBillQueries';
import { CREATE_DETAIL_ADJUSTMENT } from '../../graphql/mutations/settlement/settlementBillMutations';
import {
  parseCsvFile,
  validateDetailRows,
  downloadDetailAdjustmentTemplate,
  type ParsedDetailRow
} from '../../utils/detailAdjustmentCsv';
import {
  formatSettlementAmountFromCents,
  formatSettlementAmountFromMajor,
  getSettlementCurrencySymbol,
} from '../../utils/settlementAmountFormatter';
import {
  ADJUSTMENT_ATTACHMENT_MAX_SIZE_BYTES,
  formatAttachmentSize,
  uploadAdjustmentAttachment,
  validateAdjustmentAttachment,
  type AdjustmentAttachmentDraft,
} from '../../utils/settlementAdjustmentAttachment';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface AdjustmentFormProps {
  billId: string;
  feeItemId: string;
  feeItemName: string;
  currentAmount: number;
  allowAdjustment: boolean;
  currency: string;
  isBillCanceled?: boolean; // 账单是否已作废
  /** 是否展示「上传调账明细」Tab（DSP 场景：在弹窗内上传 CSV 并预览） */
  showDetailAdjustmentUpload?: boolean;
  /** 账期开始（ISO 字符串），用于明细调账日期校验 */
  periodStart?: string;
  /** 账期结束（ISO 字符串），用于明细调账日期校验 */
  periodEnd?: string;
  /** 是否允许上传调账附件（格式不限）。默认关闭，仅 DSP 中心页开启 */
  enableAttachment?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** 草稿模式：不直接提交调账接口，仅回传调账草稿给页面 */
  draftMode?: boolean;
  onDraftSubmit?: (draft: {
    feeItemId: string;
    adjustmentAmount: string;
    reason: string;
    beforeAmount: string;
    afterAmount: string;
    attachment: AdjustmentAttachmentDraft | null;
  }) => void;
}

interface AdjustmentFormData {
  finalAmount: number;
  reason: string;
}

const AdjustmentForm: React.FC<AdjustmentFormProps> = ({
  billId,
  feeItemId,
  feeItemName,
  currentAmount,
  allowAdjustment,
  currency,
  isBillCanceled = false,
  showDetailAdjustmentUpload = false,
  periodStart = '',
  periodEnd = '',
  enableAttachment = false,
  onSuccess,
  onCancel,
  draftMode = false,
  onDraftSubmit
}) => {
  const [form] = Form.useForm<AdjustmentFormData>();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalTab, setModalTab] = useState<string>('amount');
  const [detailRows, setDetailRows] = useState<ParsedDetailRow[]>([]);
  const [detailReason, setDetailReason] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailValidateErrors, setDetailValidateErrors] = useState<string[]>([]);
  const [attachment, setAttachment] = useState<AdjustmentAttachmentDraft | null>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createAdjustmentTask] = useMutation(CREATE_ADJUSTMENT_TASK, {
    refetchQueries: [
      {
        query: GET_ADJUSTMENT_TASKS,
        variables: { billId }
      },
      {
        query: GET_BILL_DETAIL,
        variables: { billId }
      }
    ]
  });

  const [createDetailAdjustment] = useMutation(CREATE_DETAIL_ADJUSTMENT, {
    refetchQueries: [
      { query: GET_BILL_DETAIL, variables: { billId } },
      { query: GET_ADJUSTMENT_TASKS, variables: { billId } }
    ]
  });

  const currencySymbol = getSettlementCurrencySymbol(currency);

  const handleSubmit = async (values: AdjustmentFormData) => {
    if (isBillCanceled) {
      message.error('账单已作废，无法调账');
      return;
    }
    if (!allowAdjustment) {
      message.error('该费用项不允许调账');
      return;
    }

    // 使用更精确的方法处理浮点数，避免精度问题
    // 将金额转换为字符串，然后按分处理，避免浮点数运算
    const finalAmountStr = values.finalAmount.toFixed(2);
    const finalAmountInCents = Math.round(parseFloat(finalAmountStr) * 100);
    
    // 确保 currentAmount 也是整数（如果传入的是浮点数，先四舍五入）
    const currentAmountRounded = Math.round(currentAmount);
    
    // 计算调整金额
    const adjustmentAmount = finalAmountInCents - currentAmountRounded;
    
    // 检查调整金额是否为零（允许小的误差，但不应发送接近零的值）
    if (Math.abs(adjustmentAmount) < 1) {
      message.error('调整后金额不能与当前金额相同');
      return;
    }

    console.log('调账调试信息:', {
      currentAmount,
      currentAmountRounded,
      finalAmount: values.finalAmount,
      finalAmountStr,
      finalAmountInCents,
      adjustmentAmount
    });

    setLoading(true);
    try {
      if (draftMode) {
        onDraftSubmit?.({
          feeItemId,
          adjustmentAmount: adjustmentAmount.toString(),
          reason: values.reason,
          beforeAmount: currentAmountRounded.toString(),
          afterAmount: finalAmountInCents.toString(),
          attachment: enableAttachment ? attachment : null,
        });
        message.success('已加入待审批调账清单');
        // 草稿已交给页面持有，弹窗内的附件必须清空，否则再次打开会把同一个附件重复提交
        setAttachment(null);
        setVisible(false);
        onSuccess?.();
        return;
      }
      
      const result = await createAdjustmentTask({
        variables: {
          billId,
          adjustmentRequest: {
            settlementFeeItemId: feeItemId,
            adjustmentAmount: adjustmentAmount.toString(),
            reason: values.reason
          }
        }
      });

      if (result.data?.createAdjustmentTask?.success) {
        message.success('调账成功');
        setVisible(false);
        onSuccess?.();
      } else {
        const errorMsg = result.data?.createAdjustmentTask?.errorMessage || '调账失败';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('创建调账任务失败:', error);
      message.error('创建调账任务失败: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setModalTab('amount');
    setDetailRows([]);
    setDetailReason('');
    setDetailValidateErrors([]);
    setAttachment(null);
    setAttachmentUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onCancel?.();
  };

  /** antd Upload 的展示列表由 attachment 派生，保证「上传成功才入列」 */
  const attachmentFileList: UploadFile[] = attachment
    ? [{ uid: attachment.uid, name: attachment.fileName, status: 'done' as const, size: attachment.fileSize }]
    : [];

  const handleAttachmentUpload = async (file: File, uid: string) => {
    setAttachmentUploading(true);
    try {
      const draft = await uploadAdjustmentAttachment(file, uid);
      // 每条调账只允许一个附件，重新上传即覆盖
      setAttachment(draft);
      message.success(`附件「${file.name}」上传成功`);
    } catch (err) {
      message.error(`附件「${file.name}」上传失败: ` + (err as Error).message);
    } finally {
      setAttachmentUploading(false);
    }
  };

  /** 调账附件上传区（格式不限，仅限大小；每条调账一个附件） */
  const attachmentUploadNode = (
    <Form.Item label="调账附件（选填，格式不限）" style={{ marginBottom: 16 }}>
      <Upload
        maxCount={1}
        fileList={attachmentFileList}
        beforeUpload={(file) => {
          const error = validateAdjustmentAttachment(file as File);
          if (error) {
            message.warning(error);
            return Upload.LIST_IGNORE;
          }
          void handleAttachmentUpload(file as File, (file as UploadFile).uid);
          // 由自身状态驱动展示列表，不让 antd 接管，也不走 antd 默认的 action 上传
          return Upload.LIST_IGNORE;
        }}
        onRemove={() => {
          setAttachment(null);
          return false;
        }}
      >
        <Button
          icon={<PaperClipOutlined />}
          loading={attachmentUploading}
          disabled={isBillCanceled || !allowAdjustment}
        >
          {attachment ? '重新上传' : '上传附件'}
        </Button>
      </Upload>
      <div className="text-gray-500 text-xs mt-1">
        每条调账 1 个附件，不超过 {formatAttachmentSize(ADJUSTMENT_ATTACHMENT_MAX_SIZE_BYTES)}
      </div>
    </Form.Item>
  );

  const handleDetailFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !periodStart || !periodEnd) return;
    setDetailValidateErrors([]);
    try {
      const rows = await parseCsvFile(file);
      if (rows.length === 0) {
        message.warning('未解析到有效数据行，请确认表格包含列：日期、金额');
        e.target.value = '';
        return;
      }
      const ps = periodStart.slice(0, 10);
      const pe = periodEnd.slice(0, 10);
      const { valid, errors } = validateDetailRows(rows, ps, pe);
      setDetailRows(valid);
      setDetailValidateErrors(errors);
      if (errors.length > 0) {
        message.warning(`校验未通过 ${errors.length} 条，仅展示通过的行。请修正后重新上传。`);
      } else {
        message.success(`已解析 ${valid.length} 条明细`);
      }
    } catch (err) {
      message.error('解析 CSV 失败: ' + (err as Error).message);
    }
    e.target.value = '';
  };

  const handleDetailSubmit = async () => {
    if (detailRows.length === 0) {
      message.warning('请先上传并解析 CSV，且至少有一条通过校验的明细');
      return;
    }
    if (!detailReason.trim()) {
      message.warning('请填写调账原因');
      return;
    }
    setDetailLoading(true);
    try {
      const result = await createDetailAdjustment({
        variables: {
          billId,
          request: {
            settlementFeeItemId: feeItemId,
            reason: detailReason.trim(),
            items: detailRows.map((r) => ({
              businessDate: r.businessDate,
              adjustmentAmount: String(r.amountInCents)
            }))
          }
        }
      });
      if (result.data?.createDetailAdjustment?.success) {
        message.success('明细调账提交成功');
        setVisible(false);
        setDetailRows([]);
        setDetailReason('');
        onSuccess?.();
      } else {
        message.error(result.data?.createDetailAdjustment?.errorMessage || '提交失败');
      }
    } catch (err) {
      message.error('提交失败: ' + (err as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const showForm = () => {
    if (isBillCanceled) {
      message.warning('账单已作废，无法调账');
      return;
    }
    if (!allowAdjustment) {
      message.warning('该费用项不允许调账');
      return;
    }
    setVisible(true);
  };

  const quickAdjust = (amount: number) => {
    const currentFormValue = form.getFieldValue('finalAmount') || (currentAmount / 100);
    const finalAmount = currentFormValue + amount;
    form.setFieldsValue({
      finalAmount
    });
  };

  // 每次打开弹窗时，按金额调账 Tab 的「调账后金额」初始值应为该费用项当前金额（含此前明细调账后的最新汇总）
  useEffect(() => {
    if (visible) {
      form.setFieldsValue({ finalAmount: currentAmount / 100 });
    }
  }, [visible, currentAmount]);

  return (
    <>
      <HasResource resourceCodes={[
        ResourceCode.settlement.SETTLEMENT_COST_BILL_OPERATE,
        ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE,
      ]}>
        <Button
          type="primary"
          size="small"
          onClick={showForm}
          disabled={isBillCanceled || !allowAdjustment}
          title={
            isBillCanceled
              ? '账单已作废，无法调账'
              : allowAdjustment
                ? '创建调账任务'
                : '该费用项不允许调账'
          }
        >
          调账
        </Button>
      </HasResource>

      <Modal
        title={`调账 - ${feeItemName}`}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={showDetailAdjustmentUpload ? 680 : 600}
        destroyOnClose
      >
        {showDetailAdjustmentUpload ? (
          <Tabs
            activeKey={modalTab}
            onChange={setModalTab}
            items={[
              {
                key: 'amount',
                label: '按金额调账',
                children: (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <strong>当前金额:</strong> {formatSettlementAmountFromCents(currentAmount, currency)}
                    </div>
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSubmit}
                      initialValues={{
                        finalAmount: currentAmount / 100,
                        reason: ''
                      }}
                    >
                      <Form.Item
                        label={`调整后金额 (单位: ${currency})`}
                        name="finalAmount"
                        rules={[
                          { required: true, message: '请输入调整后金额' },
                          { type: 'number', min: 0, max: 9999999.99, message: '金额超出范围' }
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="输入调整后的最终金额"
                          precision={2}
                          step={0.01}
                          addonBefore={currencySymbol}
                          formatter={(value) => {
                            if (value === undefined || value === null || value === '') return '';
                            const num = Number(value);
                            if (num % 1 === 0) return num.toString();
                            return num.toFixed(2);
                          }}
                          parser={(value) => {
                            if (!value) return '';
                            const num = parseFloat(value);
                            return isNaN(num) ? '' : num;
                          }}
                        />
                      </Form.Item>
                      <div style={{ marginBottom: 16 }}>
                        <Space>
                          <Button icon={<PlusOutlined />} onClick={() => quickAdjust(1)} size="small">+{currencySymbol}1.00</Button>
                          <Button icon={<PlusOutlined />} onClick={() => quickAdjust(5)} size="small">+{currencySymbol}5.00</Button>
                          <Button icon={<PlusOutlined />} onClick={() => quickAdjust(10)} size="small">+{currencySymbol}10.00</Button>
                          <Button icon={<PlusOutlined />} onClick={() => quickAdjust(100)} size="small">+{currencySymbol}100.00</Button>
                        </Space>
                      </div>
                      <Form.Item
                        label="调账原因"
                        name="reason"
                        rules={[{ required: true, message: '请输入调账原因' }, { max: 500, message: '原因不能超过500字符' }]}
                      >
                        <Input.TextArea rows={3} placeholder="请详细说明调账原因" maxLength={500} showCount />
                      </Form.Item>
                      {enableAttachment && attachmentUploadNode}
                      <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                          <Button onClick={handleCancel}>取消</Button>
                          <Button type="primary" htmlType="submit" loading={loading}>提交调账</Button>
                        </Space>
                      </Form.Item>
                    </Form>
                  </>
                )
              },
              {
                key: 'detail',
                label: '上传调账明细',
                children: (
                  <div className="space-y-4">
                    <p className="text-gray-600 text-sm">
                      CSV 列名为 <strong>日期</strong>、<strong>金额</strong>；日期 YYYY-MM-DD，金额数值（最多 2 位小数，可正可负）。
                    </p>
                    <Space>
                      <Button
                        type="default"
                        onClick={downloadDetailAdjustmentTemplate}
                      >
                        下载调账明细模板
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleDetailFileChange}
                      />
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isBillCanceled || !allowAdjustment}
                      >
                        选择 CSV 文件
                      </Button>
                    </Space>
                    {detailValidateErrors.length > 0 && (
                      <div className="text-amber-600 text-sm bg-amber-50 p-2 rounded">
                        <p className="font-medium mb-1">以下行未通过校验，已排除：</p>
                        <ul className="list-disc pl-5">
                          {detailValidateErrors.slice(0, 5).map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                          {detailValidateErrors.length > 5 && <li>… 共 {detailValidateErrors.length} 条</li>}
                        </ul>
                      </div>
                    )}
                    {detailRows.length > 0 && (
                      <>
                        <p className="font-medium">调账明细预览（共 {detailRows.length} 条）</p>
                        <Table
                          columns={[
                            { title: '业务发生日期', dataIndex: 'businessDate', key: 'businessDate', width: 130 },
                            {
                              title: `调账金额 (${currency})`,
                              key: 'amount',
                              width: 130,
                              render: (_: unknown, r: ParsedDetailRow) =>
                                r.adjustmentAmount >= 0
                                  ? `+${formatSettlementAmountFromMajor(r.adjustmentAmount, currency)}`
                                  : formatSettlementAmountFromMajor(r.adjustmentAmount, currency)
                            }
                          ]}
                          dataSource={detailRows.map((r, i) => ({ ...r, key: i }))}
                          pagination={false}
                          size="small"
                          scroll={{ y: 220 }}
                        />
                        <p className="text-sm text-gray-600">
                          合计：{formatSettlementAmountFromCents(detailRows.reduce((s, r) => s + r.amountInCents, 0), currency)}
                        </p>
                        <div>
                          <label className="block mb-1 font-medium">调账原因（必填）</label>
                          <Input.TextArea
                            rows={2}
                            value={detailReason}
                            onChange={(e) => setDetailReason(e.target.value)}
                            placeholder="请填写调账原因"
                            maxLength={500}
                            showCount
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button onClick={handleCancel}>取消</Button>
                          <Button type="primary" loading={detailLoading} onClick={handleDetailSubmit}>
                            提交调账
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )
              }
            ]}
          />
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>当前金额:</strong> {formatSettlementAmountFromCents(currentAmount, currency)}
            </div>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                finalAmount: currentAmount / 100,
                reason: ''
              }}
            >
              <Form.Item
                label={`调整后金额 (单位: ${currency})`}
                name="finalAmount"
                rules={[
                  { required: true, message: '请输入调整后金额' },
                  { type: 'number', min: 0, max: 9999999.99, message: '金额超出范围' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="输入调整后的最终金额"
                  precision={2}
                  step={0.01}
                  addonBefore={currencySymbol}
                  formatter={(value) => {
                    if (value === undefined || value === null || value === '') return '';
                    const num = Number(value);
                    if (num % 1 === 0) return num.toString();
                    return num.toFixed(2);
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    const num = parseFloat(value);
                    return isNaN(num) ? '' : num;
                  }}
                />
              </Form.Item>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button icon={<PlusOutlined />} onClick={() => quickAdjust(1)} size="small">+{currencySymbol}1.00</Button>
                  <Button icon={<PlusOutlined />} onClick={() => quickAdjust(5)} size="small">+{currencySymbol}5.00</Button>
                  <Button icon={<PlusOutlined />} onClick={() => quickAdjust(10)} size="small">+{currencySymbol}10.00</Button>
                  <Button icon={<PlusOutlined />} onClick={() => quickAdjust(100)} size="small">+{currencySymbol}100.00</Button>
                </Space>
              </div>
              <Form.Item
                label="调账原因"
                name="reason"
                rules={[{ required: true, message: '请输入调账原因' }, { max: 500, message: '原因不能超过500字符' }]}
              >
                <Input.TextArea rows={3} placeholder="请详细说明调账原因" maxLength={500} showCount />
              </Form.Item>
              {enableAttachment && attachmentUploadNode}
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={handleCancel}>取消</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>提交调账</Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};

export default AdjustmentForm;
